import React from 'react';
import './card.css';

/**
 * Card: a flat surface with a 1px border. No shadow, no hover lift.
 * - header / footer: free content rows
 * - title / subtitle / actions: a standard header row
 * - padding: 'none' | 'sm' | 'md' (default)
 * - interactive or onClick: gets a hover state and keyboard access
 */
export default function Card({
    header,
    footer,
    title,
    subtitle,
    actions,
    padding = 'md',
    interactive,
    className = '',
    children,
    onClick,
    onKeyDown,
    ...rest
}) {
    const clickable = interactive || typeof onClick === 'function';
    const classes = [
        'card',
        `card--pad-${padding}`,
        clickable ? 'card--interactive' : '',
        className,
    ].filter(Boolean).join(' ');

    const keyboardProps = typeof onClick === 'function' && rest.role === undefined
        ? {
            role: 'button',
            tabIndex: rest.tabIndex ?? 0,
            onKeyDown: (e) => {
                if (onKeyDown) onKeyDown(e);
                if (e.defaultPrevented) return;
                if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
                    e.preventDefault();
                    onClick(e);
                }
            },
        }
        : { onKeyDown };

    const standardHeader = (title || subtitle || actions) ? (
        <div className="card__head">
            <div className="card__head-text">
                {title && <h3 className="card__title">{title}</h3>}
                {subtitle && <p className="card__subtitle">{subtitle}</p>}
            </div>
            {actions && <div className="card__actions">{actions}</div>}
        </div>
    ) : null;

    return (
        <div className={classes} onClick={onClick} {...keyboardProps} {...rest}>
            {standardHeader}
            {header && <div className="header">{header}</div>}
            <div className="body">{children}</div>
            {footer && <div className="footer">{footer}</div>}
        </div>
    );
}
